import React from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Eye, MousePointer, Globe, TrendingUp, FileText, Calendar, Users } from 'lucide-react';
import SEO from '@/components/SEO';

const AnalyticsReports = () => {
  const metrics = [
    {
      icon: Eye,
      title: 'Media Impressions',
      description: 'Track total views and impressions across all distribution channels including news websites, social media, and direct views.'
    },
    {
      icon: MousePointer,
      title: 'Engagement Metrics',
      description: 'Monitor clicks, shares, and interactions with your press release content to measure audience engagement.'
    },
    {
      icon: Globe,
      title: 'Geographic Reach',
      description: 'See where your press release is being read with detailed geographic breakdown by country, state, and city.'
    },
    {
      icon: FileText,
      title: 'Media Pickup',
      description: 'Identify which media outlets, blogs, and news sites have published or referenced your press release.'
    },
    {
      icon: Users,
      title: 'Audience Demographics',
      description: 'Understand your audience with demographic data including industry, job titles, and company size.'
    },
    {
      icon: TrendingUp,
      title: 'Performance Trends',
      description: 'Compare performance across multiple press releases to identify what works best for your audience.'
    }
  ];

  const reportFeatures = [
    'Real-time analytics dashboard',
    'Detailed media pickup reports',
    'Traffic source analysis',
    'Engagement heatmaps',
    'Social media metrics',
    'SEO performance tracking',
    'Competitor benchmarking',
    'Custom date range reports',
    'Export to PDF and Excel',
    'Automated email reports'
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Analytics & Reports"
        description="Track your press release performance with real-time analytics. Monitor views, engagement, media pickups, and audience demographics on Ixoraly."
        path="/analytics-reports"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="analytics-heading">
              Analytics & Performance Reports
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Comprehensive analytics and detailed reporting to measure the impact and reach of your press release distribution.
            </p>
            <Link
              to="/pricing"
              className="inline-flex items-center px-8 py-4 bg-coral-600 text-white rounded-lg font-semibold hover:bg-coral-700 transition-colors"
            >
              Get Started with Analytics
            </Link>
          </div>
        </div>
      </section>

      {/* Metrics Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What We Track
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Get complete visibility into your press release performance with comprehensive metrics and insights
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {metrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-white rounded-xl border-2 border-gray-100 hover:border-navy-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-navy-100 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="text-navy-600" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{metric.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{metric.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sample Dashboard */}
      <section className="py-20 bg-navy-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Real-Time Analytics Dashboard
            </h2>
            <p className="text-lg text-gray-600">
              Access your press release performance data 24/7 through our intuitive dashboard
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <BarChart3 className="text-blue-600" size={32} />
                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">+24%</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">45,892</h3>
              <p className="text-sm text-gray-600">Total Impressions</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <FileText className="text-purple-600" size={32} />
                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">+12%</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">127</h3>
              <p className="text-sm text-gray-600">Media Pickups</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <MousePointer className="text-orange-600" size={32} />
                <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">+8%</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">3,421</h3>
              <p className="text-sm text-gray-600">Click-Through Rate</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <Globe className="text-green-600" size={32} />
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded">28</span>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-1">42</h3>
              <p className="text-sm text-gray-600">Countries Reached</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Report Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reportFeatures.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-navy-600 rounded-full"></div>
                  <span className="text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How Our Analytics Work
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-navy-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Distribute</h3>
              <p className="text-gray-600">Your press release is distributed across our network</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-navy-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Track</h3>
              <p className="text-gray-600">Our system monitors all interactions and pickups</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-navy-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Analyze</h3>
              <p className="text-gray-600">Data is processed and converted into insights</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-navy-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Report</h3>
              <p className="text-gray-600">Receive comprehensive reports in your dashboard</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Start Tracking Your PR Performance
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Get detailed analytics and insights with every press release distribution package.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/submit-press-release"
              className="inline-flex items-center justify-center px-8 py-4 bg-coral-600 text-white rounded-lg font-semibold hover:bg-coral-700 transition-colors"
            >
              Submit Press Release
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 transition-colors border border-white/20"
            >
              View Pricing Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AnalyticsReports;
